import { IPaginationResponse } from '@/interfaces/common.interface';
import { ECourseStatus } from '@/interfaces/doccen-lib/contentManagement.interface';
import {
  ICourseEnterprise,
  THighlightCourses,
  TRecommendCourses,
} from '@/interfaces/enterprise-profile/course_tab.interface';
import { QueryFunctionContext, QueryKey } from 'react-query';
import { tmsHTTPRequest } from 'services/axios/httpRequest';
import { mapQueryKeyToSearchInGroupData } from '../../common/search/generateSearchApiQuery';
import {
  ECourseType,
  ICertItem,
  ICompletedCourseResult,
  ICourse,
  ICoursePriceResponse,
  ICreateCoursePayload,
  IMinimizedCourseItem,
  ITrashCourse,
  IUpdateCoursePayload,
  TRecommendQuizCourses,
} from '../../interfaces/course.interface';
import {
  CourseStatusEnum,
  ICourseDetailResponse,
  ICourseSearchData,
} from '../../interfaces/training.interface';

export const hardDeleteCourse = (id: string) => {
  return tmsHTTPRequest.delete(`/v1/courses/hard-delete/${id}`);
};
export const restoreCourse = (id: string, data = null) => {
  return tmsHTTPRequest.put(`/v1/courses/restore/${id}`, data);
};

// export const getAllCourseWithStatus = ({
//   pageParam = 1,
//   queryKey,
// }: QueryFunctionContext<any[], any>) => {
//   return tmsHTTPRequest.get(
//     `/v1/courses?Status=${courseStatusEnumMapping[queryKey[0]]}&FolderId=${
//       queryKey[1]
//     }&PageNumber=${pageParam}&PageSize=${queryKey[2] || 4}`
//   );
// };

export const getAllCourseInTrash = ({
  queryKey: _queryKey,
}: QueryFunctionContext<any[], any>) => {
  return tmsHTTPRequest.get<IPaginationResponse<ITrashCourse>>(
    `/v1/courses/trash`
  );
};

export const postNewCourse = (data: ICreateCoursePayload) => {
  return tmsHTTPRequest.post<{
    courseId: string;
  }>('/v1/courses', data);
};

export const getCourseDetails = (courseId: string) => {
  return tmsHTTPRequest.get<ICourseDetailResponse>(`/v1/courses/${courseId}`);
};

export const putCourseStatus = (data: { id: string; status: string }) => {
  if (data.status == CourseStatusEnum.Hidden)
    return tmsHTTPRequest.put('/v1/courses/hide', {
      courseId: data.id,
      status: data.status,
    });
  return tmsHTTPRequest.put('/v1/courses/publish', data);
};

export const getListCourseCert = () => {
  return tmsHTTPRequest.get<ICertItem[]>('/v1/courses/certificate');
};

export const putCourseInfo = (data: IUpdateCoursePayload) => {
  return tmsHTTPRequest.put(`/v1/courses/${data.id}`, data);
};

export const putCourseThumbnail = (file: File, id: string) => {
  return tmsHTTPRequest.put(
    `/v1/courses/${id}/thumbnail`,
    { file },
    {
      headers: {
        'content-type': 'multipart/form-data',
      },
    }
  );
};

export const putSoftDeleteCourse = (id: string) => {
  return tmsHTTPRequest.put(`/v1/courses/soft-delete/${id}`, null);
};

export const getCourseDetailOwnerView = (id: string) => {
  return tmsHTTPRequest.get<ICourseDetailResponse>(`/v1/courses/${id}/manage`);
};

export const postSearchCourseInTrash = (data: ICourseSearchData) => {
  return tmsHTTPRequest.post<IPaginationResponse<ITrashCourse>>(
    `/v1/courses/trash/search`,
    {
      ...data,
      advancedSearch: { fields: ['Title'] },
    }
  );
};

export const middlewarePostSearchCourseInTrash = ({
  queryKey: _queryKey,
}: QueryFunctionContext<any[], any>) => {
  return postSearchCourseInTrash({
    keyword: _queryKey[1],
  });
};

export const getAllRequestCertificatesOrCompletedCourses = ({
  courseId,
  status,
  pageNumber,
  pageSize,
}: {
  courseId: string | number;
  status: 'CompletedCourse' | 'RequestedCertificate'; // 0 = CompletedCourse, 1 = RequestedCertificate
  pageNumber: string | number;
  pageSize: string | number;
}) => {
  return tmsHTTPRequest.get(
    `/v1/courses/${courseId}/certificate?CourseId=${courseId}&Status=${status}&PageNumber=${pageNumber}&PageSize=${pageSize}`
  );
};
export const rejectCertificateRequest = (data: {
  courseId: string;
  userId: string;
}) => {
  return tmsHTTPRequest.put(
    `/v1/courses/${data.courseId}/certificate/reject`,
    data
  );
};
export const exportCertificateRequest = (id: string) => {
  return tmsHTTPRequest.post(
    `/v1/courses/${id}/certificate/request/export`,
    null,
    { responseType: 'blob' }
  );
};
export const exportCourseCompleted = (id: string) => {
  return tmsHTTPRequest.post(`/v1/courses/${id}/complete/export`, null, {
    responseType: 'blob',
  });
};
export const putCompleteCourse = (courseId: string) => {
  return tmsHTTPRequest.put(`/v1/courses/${courseId}/complete`, {});
};

export const putCertificateRequest = (data: FormData, courseId: string) => {
  return tmsHTTPRequest.put(
    `/v1/courses/${courseId}/certificate/request`,
    data,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    }
  );
};

export const getCertificateRequest = (courseId: string) => {
  return tmsHTTPRequest.get(`/v1/courses/${courseId}/certificate/requested`);
};

export const postSearchCourseInFolder = (
  context: QueryFunctionContext<QueryKey>
) => {
  const data = mapQueryKeyToSearchInGroupData(context);
  return tmsHTTPRequest.post(`/v1/courses/${data.folderId}/search`, data);
};

export const getCoursesForCoursePage = ({
  pageParam = 1,
  queryKey,
}: QueryFunctionContext<any[], any>) => {
  return tmsHTTPRequest.get<IPaginationResponse<ICourse>>(
    `/v1/courses/user?Type=${queryKey[1]}&PageNumber=${pageParam}&PageSize=${
      queryKey[2] || 4
    }`
  );
};

export const putArrangeCourseMetarials = (
  courseId: string,
  data: {
    courseId: string;
    materials: { id: string; type: number }[];
  }
) => {
  return tmsHTTPRequest.put(`/v1/courses/${courseId}/arrange-material`, data);
};

export const getDefaultInfoForRequestCertificateForm = (courseId: string) => {
  return tmsHTTPRequest.get<{
    email: string;
    fullName: string;
    ownerId: number;
    phoneNumber: string;
    deliveryAddress: string;
    dateOfBirth: string;
    academicDegree: string;
    idCardIssuedDate: string;
    idCardIssuedPlace: string;
    idCardNumber: string;
    medicalLicenseIssuedDate: string;
    medicalLicenseNumber: string;
    residentialAddress: string;
  }>(`/v1/courses/${courseId}/certificate/autofill`);
};

/**
 * The function `getCompletedCourseResult` makes an HTTP GET request to retrieve the completed result
 * of a course with a given courseId.
 * @param {string} courseId - The `courseId` parameter is a string that represents the unique
 * identifier of a course. It is used to specify which course's completed result should be retrieved.
 * @returns a Promise that resolves to an object of type `ICompletedCourseResult`.
 */
export const getCompletedCourseResult = (courseId: string) => {
  return tmsHTTPRequest.get<ICompletedCourseResult>(
    `/v1/courses/${courseId}/completed/result`
  );
};

export const putConfirmAgreementOfCourse = (courseId: string) =>
  tmsHTTPRequest.put<string>(`/v1/courses/${courseId}/agreement/confirm`, null);

/** querykey must follow format [unique_key, userId: string, certificateName: CourseCertificateEnum | null, pageSize: string] */
export const getCoursesEnterpriseByUserId = ({
  pageParam = 1,
  queryKey,
}: QueryFunctionContext<(string | number)[], number>) => {
  return tmsHTTPRequest.get<IPaginationResponse<ICourseEnterprise>>(
    `/v1/courses/enterprise/${queryKey[1]}?PageNumber=${pageParam}${
      queryKey[3] !== undefined ? `&PageSize=${queryKey[3] || 10}` : ''
    }&${queryKey[2] ? `certificateName=${queryKey[2]}` : ''}`
  );
};

export const getRecommendCoursesOfEnterpriseUser = (
  userId: string,
  courseIds: string[]
) =>
  tmsHTTPRequest.get<TRecommendCourses>(
    `/v1/courses/enterprise/${userId}/recommend?${courseIds
      .map((courseId) => `courseIds=${courseId}`)
      .join('&')}`
  );

export const getHighlightCoursesOfEnterpriseUser = (userId: string) =>
  tmsHTTPRequest.get<THighlightCourses>(
    `/v1/courses/enterprise/${userId}/highlight`
  );

export const getRecommendationQuizCourses = () =>
  tmsHTTPRequest.get<TRecommendQuizCourses>(
    `/v1/courses/recommendation/quizzes`
  );

export const getCoursePrice = (courseId: string) => {
  return tmsHTTPRequest.get<ICoursePriceResponse>(
    `/v1/courses/${courseId}/price`
  );
};

export const postCoursePrice = (data: {
  courseId: string;
  price: number;
  isPaid: boolean;
}) => {
  return tmsHTTPRequest.post<number>(
    `/v1/courses/${data.courseId}/price`,
    data
  );
};

export const getSearchCourses = ({
  pageNumber,
  pageSize,
  categories,
  query,
  type,
}: {
  categories: number[] | null;
  pageSize: number;
  pageNumber: number;
  query?: string;
  type?: ECourseType;
}) => {
  const searchParams = new URLSearchParams({
    pageSize: pageSize.toString(),
    pageNumber: pageNumber.toString(),
  });

  if (categories) {
    categories.forEach((item) =>
      searchParams.append('categories', item.toString())
    );
  }

  if (query) {
    searchParams.append('query', query);
  }

  if (type) {
    searchParams.append('type', type);
  }

  return tmsHTTPRequest.get<IPaginationResponse<IMinimizedCourseItem>>(
    `/v1/courses/search?${searchParams.toString()}`
  );
};

export const getIndependentCoursesByOwner = (body: {
  status: ECourseStatus;
  pageSize: number;
  pageNumber: number;
  categoryId: number | null;
}) =>
  tmsHTTPRequest.get<IPaginationResponse<IMinimizedCourseItem>>(`/v1/courses`, {
    params: {
      pageNumber: body.pageNumber.toString(),
      pageSize: body.pageSize.toString(),
      status: body.status,
      categoryId: body.categoryId,
    },
  });
